import React, { useState, useEffect, createContext, useContext } from "react";

// =============================================================================
// NOTIFICATION CONTEXT & PROVIDER
// =============================================================================

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationProvider"
    );
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const addNotification = (notification) => {
    const id = Date.now() + Math.random();
    const newNotification = {
      id,
      type: "success", // success, error, warning, info
      duration: 5000,
      ...notification,
    };

    setNotifications((prev) => [...prev, newNotification]);

    // Auto remove after duration
    setTimeout(() => {
      removeNotification(id);
    }, newNotification.duration);
  };

  const removeNotification = (id) => {
    setNotifications((prev) => prev.filter((notif) => notif.id !== id));
  };

  const showSuccess = (title, message) => {
    addNotification({
      type: "success",
      title,
      message,
      icon: "✅",
    });
  };

  const showError = (title, message) => {
    addNotification({
      type: "error",
      title,
      message,
      icon: "❌",
    });
  };

  const showWarning = (title, message) => {
    addNotification({
      type: "warning",
      title,
      message,
      icon: "⚠️",
    });
  };

  const showInfo = (title, message) => {
    addNotification({
      type: "info",
      title,
      message,
      icon: "ℹ️",
    });
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        addNotification,
        removeNotification,
        showSuccess,
        showError,
        showWarning,
        showInfo,
      }}
    >
      {children}
      <NotificationContainer />
    </NotificationContext.Provider>
  );
};

// =============================================================================
// NOTIFICATION COMPONENTS
// =============================================================================

const NotificationContainer = () => {
  const { notifications } = useNotification();

  return (
    <div className="fixed bottom-4 right-4 z-50 space-y-3 max-w-sm w-full">
      {notifications.map((notification) => (
        <NotificationCard key={notification.id} notification={notification} />
      ))}
    </div>
  );
};

const NotificationCard = ({ notification }) => {
  const { removeNotification } = useNotification();
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    // Trigger entrance animation
    setTimeout(() => setIsVisible(true), 10);
  }, []);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => {
      removeNotification(notification.id);
    }, 300);
  };

  const getNotificationStyles = () => {
    const baseStyles = "border-l-4 shadow-lg";

    switch (notification.type) {
      case "success":
        return `${baseStyles} bg-green-50 border-green-400 text-green-800`;
      case "error":
        return `${baseStyles} bg-red-50 border-red-400 text-red-800`;
      case "warning":
        return `${baseStyles} bg-yellow-50 border-yellow-400 text-yellow-800`;
      case "info":
        return `${baseStyles} bg-blue-50 border-blue-400 text-blue-800`;
      default:
        return `${baseStyles} bg-gray-50 border-gray-400 text-gray-800`;
    }
  };

  const getProgressBarColor = () => {
    switch (notification.type) {
      case "success":
        return "bg-green-400";
      case "error":
        return "bg-red-400";
      case "warning":
        return "bg-yellow-400";
      case "info":
        return "bg-blue-400";
      default:
        return "bg-gray-400";
    }
  };

  return (
    <div
      className={`
        relative overflow-hidden rounded-lg p-4 transition-all duration-300 ease-in-out transform
        ${getNotificationStyles()}
        ${
          isVisible && !isLeaving
            ? "translate-x-0 opacity-100 scale-100"
            : "translate-x-full opacity-0 scale-95"
        }
      `}
    >
      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 h-1 bg-black bg-opacity-10 w-full">
        <div
          className={`h-full ${getProgressBarColor()} transition-all ease-linear`}
          style={{
            animation: `shrink ${notification.duration}ms linear forwards`,
          }}
        />
      </div>

      {/* Content */}
      <div className="flex items-start space-x-3">
        {/* Icon */}
        <div className="flex-shrink-0 text-xl">{notification.icon}</div>

        {/* Text content */}
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold">{notification.title}</h4>
          {notification.message && (
            <p className="text-sm opacity-90 mt-1">{notification.message}</p>
          )}
        </div>

        {/* Close button */}
        <button
          onClick={handleClose}
          className="flex-shrink-0 text-lg opacity-60 hover:opacity-100 transition-opacity"
        >
          ×
        </button>
      </div>
    </div>
  );
};

// Add CSS animation for progress bar
if (typeof document !== "undefined") {
  const style = document.createElement("style");
  style.textContent = `
    @keyframes shrink {
      from { width: 100%; }
      to { width: 0%; }
    }
  `;
  document.head.appendChild(style);
}
